/**
 * Which country a visitor is shopping in, and what that changes.
 *
 * Two things follow from it. Prices on the public pages are written in rupees for India and in dollars for everywhere else, and a
 * candidate who signs up opens Browse on the jobs of the country they chose (the United States or India), or on every country when
 * they chose "Other countries". Only the United States and India are defaulted, the same two markets `services/jobCountry.js` knows.
 *
 * The country is worked out in this order, and the first answer wins:
 *
 *   1. what the visitor picked in the country menu (kept in this browser)
 *   2. the region of the browser's language (`en-IN` is India, `en-US` is the United States, `en-GB` is somewhere else)
 *   3. the browser's time zone, for a language that names no region
 *   4. "Other countries", which shows dollars and filters no jobs
 *
 * Rupees are a display only. Every charge is made in US dollars (services/billing.js), so a price in rupees is never what a card is
 * charged: the page says so beside the price (`CHARGED_IN_USD`).
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const MARKETS = {
  IN: { code: 'IN', label: 'India', currency: 'INR' },
  US: { code: 'US', label: 'United States', currency: 'USD' },
  OTHER: { code: 'OTHER', label: 'Other countries', currency: 'USD' },
};

/** The order they are offered in. */
export const MARKET_CODES = ['IN', 'US', 'OTHER'];

/** Rupees to a dollar on the public pages: a round list price, not the exchange rate of the day. */
export const INR_PER_USD = 100;

/** Said beside a price shown in rupees, because the card is charged in dollars. */
export const CHARGED_IN_USD = `Prices are shown at ₹${INR_PER_USD} to the dollar. Your card is charged in US dollars.`;

const PICK_KEY = 'ituka.country';
const PENDING_KEY = 'ituka.country.pending';

const IN_ZONES = ['Asia/Kolkata', 'Asia/Calcutta'];

/** The region of a language tag (`en-IN` gives `IN`), or null when it names none. Scripts (`zh-Hans-CN`) are skipped over. */
export const regionOf = (tag) => {
  const found = /^[a-z]{2,3}(?:-[A-Za-z]{4})?-([A-Za-z]{2}|\d{3})$/.exec(String(tag || ''));
  return found ? found[1].toUpperCase() : null;
};

const isUsZone = (zone) => /^(America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Boise|Juneau|Sitka|Nome|Adak|Yakutat|Metlakatla|Menominee|Indianapolis|Fort_Wayne|Knox_IN|Louisville|Shiprock|Atka|Indiana\/.+|Kentucky\/.+|North_Dakota\/.+)|Pacific\/(Honolulu|Johnston)|US\/.+)$/.test(String(zone || ''));

/** The market for a browser's languages and time zone. Pure, so it is easy to test; `detect()` reads the real browser. */
export const marketFor = ({ languages = [], timeZone = null } = {}) => {
  for (const tag of languages) {
    const region = regionOf(tag);
    if (region) return MARKETS[region] ? region : 'OTHER';
  }
  if (IN_ZONES.includes(timeZone)) return 'IN';
  if (isUsZone(timeZone)) return 'US';
  return 'OTHER';
};

export const detect = () => {
  let timeZone = null;
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* no zone: falls through to Other */ }
  const nav = typeof navigator === 'undefined' ? {} : navigator;
  return marketFor({ languages: nav.languages?.length ? nav.languages : [nav.language], timeZone });
};

const read = (key) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const write = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key); else window.localStorage.setItem(key, value);
  } catch { /* private window or blocked storage: the pick lasts until the page is closed */ }
};

/** The visitor's own pick, or null if they have not made one. */
export const savedPick = () => {
  const pick = read(PICK_KEY);
  return MARKETS[pick] ? pick : null;
};

/** The pick not yet handed to the server (it is handed over once, when a candidate signs in). */
export const pendingPick = () => (read(PENDING_KEY) === '1' ? savedPick() : null);
export const clearPendingPick = () => write(PENDING_KEY, null);

/** The value `PUT /api/candidate/jobs/market` takes for a market: a country, or ALL for every country. */
export const choiceFor = (code) => (code === 'OTHER' ? 'ALL' : code);

/**
 * A price in cents as the visitor reads it: `$10` or `₹1,000`. Whole amounts drop their `.00`.
 *
 * Rupees are one rupee to every cent (₹100 to the dollar), so 1000 cents is ₹1,000 and 200 cents is ₹200.
 */
export const formatPrice = (cents, code = 'OTHER') => {
  if (!Number.isFinite(cents)) return null;
  if (code === 'IN') {
    const rupees = (cents / 100) * INR_PER_USD;
    const whole = Number.isInteger(rupees);
    return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`;
  }
  return `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;
};

const MarketContext = createContext(null);

export const MarketProvider = ({ children }) => {
  const [code, setCode] = useState(() => savedPick() || detect());
  const [picked, setPicked] = useState(() => savedPick() !== null);

  const choose = useCallback((next) => {
    if (!MARKETS[next]) return;
    setCode(next);
    setPicked(true);
    write(PICK_KEY, next);
    write(PENDING_KEY, '1');
  }, []);

  const value = useMemo(() => ({
    code,
    market: MARKETS[code],
    picked,
    choose,
    price: (cents) => formatPrice(cents, code),
  }), [code, picked, choose]);

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
};

/** Without a provider (a test of some other screen) the market is "Other countries": dollars, as every page was before this. */
const FALLBACK = {
  code: 'OTHER', market: MARKETS.OTHER, picked: false, choose: () => {}, price: (cents) => formatPrice(cents, 'OTHER'),
};

export const useMarket = () => useContext(MarketContext) || FALLBACK;
